import { FileData } from '../Components/Utils/FileItem/FileItem';

export type CoachQuality = 'strong' | 'good' | 'needsWork' | 'empty';

export type CoachHighlightKind = 'improve' | 'unsupported' | 'conflict';

export interface CoachHighlight {
  /** Verbatim span copied from the answer text. */
  text: string;
  guidance: string;
  kind: CoachHighlightKind;
}

export interface CoachEvidenceConflict {
  /** Verbatim span from the answer that mismatches the evidence. */
  text: string;
  evidenceFile?: string;
  detail: string;
}

export interface CoachFeedback {
  quality: CoachQuality;
  summary: string;
  missing: string[];
  highlights: CoachHighlight[];
  evidenceConflicts: CoachEvidenceConflict[];
}

export interface BuildCoachPromptParams {
  areaId: string;
  criteriaId: string;
  criteriaTitle: string;
  fullCriteria: string;
  questionText: string;
  answerText: string;
  evidenceFiles: FileData[];
}

export function buildCoachPrompt({
  criteriaTitle,
  fullCriteria,
  questionText,
  answerText,
  evidenceFiles,
}: BuildCoachPromptParams): string {
  const guidanceText = fullCriteria.trim() || 'Not provided.';
  const hasGuidance = guidanceText !== 'Not provided.';

  const guidanceSection = hasGuidance
    ? `Criteria guidance (optional context for what "relevant data" means for this question — use it to judge relevance, not writing style):
"""
${guidanceText}
"""`
    : `No criteria guidance was provided. Coach the answer against the question only.`;

  const evidenceSection =
    evidenceFiles.length > 0
      ? `The user has selected the following Auto-fill evidence files. Soft-check the answer against them as optional suggestions only — mismatches are fine and must NOT lower quality:
${evidenceFiles.map((file) => `- ${file.fileName} (id: ${file.id})`).join('\n')}

Read them with ONE retrieve_library_files_content call that includes every file id (skip files you have already read earlier in this conversation). Put soft tips in "evidenceConflicts"; use an empty array when there is nothing worth suggesting.`
      : `No Auto-fill evidence files are selected, so skip the evidence cross-check and return an empty "evidenceConflicts" array.`;

  const relevanceFocus = hasGuidance
    ? 'Coach how well the answer supplies relevant data for this question, using the guidance above only as context for relevance.'
    : 'Coach how well the answer supplies relevant data for this question.';

  return `You are Mia, coaching a user during the unvalidated / data-gathering stage of a diagnostic questionnaire.

This is NOT the publishable summary stage. Informal notes and data dumps are fine when they contain relevant information. Do NOT criticise "data dump", "narrative dump", or informal note style. Do NOT demand a concise evaluative summary, strengths-gaps-recommendations form, or polished prose — that belongs on the summary page.

Criteria: "${criteriaTitle}"

${guidanceSection}

Judge ONLY the live answer text below (it may differ from any stored answer):

Question: "${questionText}"

Answer:
"""
${answerText}
"""

${evidenceSection}

${relevanceFocus} Flag missing relevant information relative to the question${hasGuidance ? ' and guidance' : ''}; do not grade writing style or form.

Return ONLY a JSON object with this exact shape:

{
  "quality": "strong" | "good" | "needsWork",
  "summary": "one honest, constructive sentence about whether the answer has relevant data for the question",
  "missing": ["relevant information for the question that is absent from the answer"],
  "highlights": [
    { "text": "exact verbatim span copied from the answer", "guidance": "short advice about this span", "kind": "improve" | "unsupported" }
  ],
  "evidenceConflicts": [
    { "text": "exact verbatim span from the answer", "evidenceFile": "file name", "detail": "soft tip about what the evidence says" }
  ]
}

Quality rubric (relevance of data, not prose quality):
- "strong": The answer includes the relevant information needed for this question${hasGuidance ? ' (given the guidance context)' : ''}, with enough specific detail to be useful. At most one minor gap.
- "good": The answer addresses the core of the question but omits ONE significant piece of relevant information, OR lacks useful specific detail.
- "needsWork": The answer omits TWO OR MORE significant pieces of relevant information, is too vague to use, or does not meaningfully address the question.

Rules:
- Never criticise informal notes, lists, or "data dump" style when relevant information is present.
- Never ask the user to rewrite into an evaluative summary.
- Every item in "missing" must be relevant information for the question${hasGuidance ? ' (optionally informed by guidance)' : ''}, not a style complaint.
- If you list 2+ items in "missing", quality MUST be "needsWork". If you list exactly 1 item in "missing", quality MUST NOT be "strong".
- "highlights[].text" and "evidenceConflicts[].text" MUST be exact verbatim substrings of the answer above, copied character-for-character.
- Include at most 4 highlights and at most 4 missing points; only the most important. Use empty arrays when there is nothing worth flagging.
- Use kind "unsupported" for claims the selected evidence does not back up, "improve" for vague or incomplete statements that need more relevant detail.
- "evidenceConflicts" are optional soft suggestions only. Do NOT lower "quality" because of evidence mismatches or missing evidence. Use an empty array when there are no files or nothing worth suggesting.
- Do not include any text outside the JSON object.`;
}

/**
 * Enforces consistency between the reported quality and the gaps found.
 * The model sometimes rates "good" despite listing many missing themes.
 * Evidence conflicts are soft suggestions and must not downgrade quality.
 */
export function normalizeCoachQuality(feedback: CoachFeedback): CoachFeedback {
  const missingCount = feedback.missing.length;
  const improveHighlights = feedback.highlights.filter((h) => h.kind === 'improve').length;
  let quality = feedback.quality;

  if (missingCount >= 2 || improveHighlights >= 3) {
    quality = 'needsWork';
  } else if (missingCount === 1 && quality === 'strong') {
    quality = 'good';
  }

  return quality === feedback.quality ? feedback : { ...feedback, quality };
}

const QUALITIES: CoachQuality[] = ['strong', 'good', 'needsWork', 'empty'];
const HIGHLIGHT_KINDS: CoachHighlightKind[] = ['improve', 'unsupported', 'conflict'];

const asStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string' && v.trim().length > 0) : [];

export function parseCoachFeedback(raw: string): CoachFeedback | null {
  let text = raw.trim();
  const codeBlockMatch = text.match(/```(?:json)?\s*([\s\S]*?)```/);
  if (codeBlockMatch) {
    text = codeBlockMatch[1].trim();
  }

  // The agent occasionally wraps the JSON in prose; grab the outermost object.
  if (!text.startsWith('{')) {
    const firstBrace = text.indexOf('{');
    const lastBrace = text.lastIndexOf('}');
    if (firstBrace === -1 || lastBrace <= firstBrace) {
      return null;
    }
    text = text.slice(firstBrace, lastBrace + 1);
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return null;
  }

  const obj = parsed as Record<string, unknown>;

  const quality = QUALITIES.includes(obj.quality as CoachQuality)
    ? (obj.quality as CoachQuality)
    : null;
  if (!quality) {
    return null;
  }

  const highlights: CoachHighlight[] = Array.isArray(obj.highlights)
    ? obj.highlights.flatMap((entry): CoachHighlight[] => {
        if (typeof entry !== 'object' || entry === null) return [];
        const h = entry as Record<string, unknown>;
        if (typeof h.text !== 'string' || !h.text.trim()) return [];
        return [
          {
            text: h.text,
            guidance: typeof h.guidance === 'string' ? h.guidance : '',
            kind: HIGHLIGHT_KINDS.includes(h.kind as CoachHighlightKind)
              ? (h.kind as CoachHighlightKind)
              : 'improve',
          },
        ];
      })
    : [];

  const evidenceConflicts: CoachEvidenceConflict[] = Array.isArray(obj.evidenceConflicts)
    ? obj.evidenceConflicts.flatMap((entry): CoachEvidenceConflict[] => {
        if (typeof entry !== 'object' || entry === null) return [];
        const c = entry as Record<string, unknown>;
        if (typeof c.text !== 'string' || !c.text.trim()) return [];
        return [
          {
            text: c.text,
            evidenceFile: typeof c.evidenceFile === 'string' ? c.evidenceFile : undefined,
            detail: typeof c.detail === 'string' ? c.detail : '',
          },
        ];
      })
    : [];

  return {
    quality,
    summary: typeof obj.summary === 'string' ? obj.summary : '',
    missing: asStringArray(obj.missing),
    highlights,
    evidenceConflicts,
  };
}

export interface CoachHighlightRange {
  start: number;
  end: number;
  kind: CoachHighlightKind;
  guidance: string;
}

/**
 * Combines highlights and evidence conflicts into a single annotation list.
 * Conflicts double as highlights so they get marked in the answer text too.
 */
export function collectCoachHighlights(feedback: CoachFeedback): CoachHighlight[] {
  const conflictHighlights: CoachHighlight[] = feedback.evidenceConflicts.map((conflict) => ({
    text: conflict.text,
    guidance: conflict.evidenceFile
      ? `${conflict.detail} (${conflict.evidenceFile})`
      : conflict.detail,
    kind: 'conflict',
  }));

  return [...feedback.highlights, ...conflictHighlights];
}

/**
 * Locates each highlight span verbatim in the answer text. Spans that no longer
 * match (e.g. the user kept typing) are silently dropped, and overlapping
 * matches are discarded so ranges never intersect.
 */
export function findCoachHighlightRanges(
  answerText: string,
  highlights: CoachHighlight[]
): CoachHighlightRange[] {
  const ranges: CoachHighlightRange[] = [];

  for (const highlight of highlights) {
    const start = answerText.indexOf(highlight.text);
    if (start === -1) {
      continue;
    }
    const end = start + highlight.text.length;
    const overlaps = ranges.some((r) => start < r.end && end > r.start);
    if (!overlaps) {
      ranges.push({ start, end, kind: highlight.kind, guidance: highlight.guidance });
    }
  }

  return ranges.sort((a, b) => a.start - b.start);
}
